// src/utils/childrenHelper.js

/**
 * BACKWARD COMPATIBILITY HELPER
 * 
 * This helper ensures components work with BOTH data structures:
 * - OLD: Single child at root level (childName, dateOfBirth, course)
 * - NEW: children array with multiple children, each with their own course
 * 
 * HOW IT WORKS:
 * - If user has NEW structure (children array), return it directly
 * - If user has OLD structure (single child at root), convert it to children array format
 * - This lets all components use the same code regardless of user data structure
 */

/**
 * Get children array from user profile (works for both old and new structures)
 * @param {Object} userProfile - User document from Firestore
 * @returns {Array} Array of children objects
 */
export const getChildren = (userProfile) => {
  if (!userProfile) return [];

  // NEW STRUCTURE: User has children array
  if (userProfile.children && Array.isArray(userProfile.children)) {
    return userProfile.children;
  }

  // OLD STRUCTURE: Convert single child to array format
  if (userProfile.childName || userProfile.dateOfBirth || userProfile.course) {
    return [{
      id: 'legacy_child', // Unique ID for legacy child
      name: userProfile.childName || '',
      dateOfBirth: userProfile.dateOfBirth || null,
      createdAt: userProfile.createdAt || new Date().toISOString(),
      order: 0,
      course: userProfile.course || null
    }];
  }

  // No children data at all
  return [];
};

/**
 * Get a specific child by ID
 * @param {Object} userProfile - User document from Firestore
 * @param {string} childId - Child ID to find
 * @returns {Object|null} Child object or null if not found
 */
export const getChildById = (userProfile, childId) => {
  const children = getChildren(userProfile);
  return children.find(child => child.id === childId) || null;
};

/**
 * Get children with active courses (not expired or pending)
 * @param {Object} userProfile - User document from Firestore
 * @returns {Array} Array of children with active courses
 */
export const getActiveChildren = (userProfile) => {
  const children = getChildren(userProfile);
  return children.filter(child => {
    if (!child.course) return false;
    const status = child.course.status;
    return status === 'active';
  });
};

/**
 * Get children with expired courses
 * @param {Object} userProfile - User document from Firestore
 * @returns {Array} Array of children with expired courses
 */
export const getExpiredChildren = (userProfile) => {
  const children = getChildren(userProfile);
  return children.filter(child => {
    if (!child.course) return false;
    const status = child.course.status;
    return status === 'expired';
  });
};

/**
 * Get children with pending courses (payment not completed)
 * @param {Object} userProfile - User document from Firestore
 * @returns {Array} Array of children with pending courses
 */
export const getPendingChildren = (userProfile) => {
  const children = getChildren(userProfile);
  return children.filter(child => {
    if (!child.course) return false;
    const status = child.course.status;
    return status === 'pending';
  });
};

/**
 * Get children eligible for renewal (any course with active, completed, or expired status).
 * Parents can renew at any time — no date gating, no early-renewal-window check.
 * Pending children are excluded because they haven't completed their first payment yet.
 * @param {Object} userProfile - User document from Firestore
 * @returns {Array} Array of children eligible for renewal
 */
export const getRenewableChildren = (userProfile) => {
  const children = getChildren(userProfile);

  return children.filter(child => {
    if (!child.course) return false;
    return child.course.status === 'active' ||
           child.course.status === 'completed' ||
           child.course.status === 'expired';
  });
};

/**
 * Get children with completed courses (all sessions attended)
 * @param {Object} userProfile - User document from Firestore
 * @returns {Array} Array of children with completed courses
 */
export const getCompletedChildren = (userProfile) => {
  const children = getChildren(userProfile);
  return children.filter(child => {
    if (!child.course) return false;
    return child.course.status === 'completed';
  });
};

/**
 * Check if user has old data structure (for migration detection)
 * @param {Object} userProfile - User document from Firestore
 * @returns {boolean} True if user has old structure
 */
export const hasLegacyStructure = (userProfile) => {
  if (!userProfile) return false;
  
  // Has children array = new structure
  if (userProfile.children && Array.isArray(userProfile.children)) {
    return false;
  }

  // Has old structure fields = legacy
  return !!(userProfile.childName || userProfile.dateOfBirth || userProfile.course);
};

/**
 * Calculate child's age from date of birth
 * @param {string|Date|Object} dateOfBirth - Date of birth (ISO string, Date object, or Firestore Timestamp)
 * @returns {number|null} Age in years or null if invalid
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  let birthDate;
  
  // Handle Firestore Timestamp
  if (dateOfBirth && typeof dateOfBirth === 'object' && dateOfBirth.seconds) {
    birthDate = new Date(dateOfBirth.seconds * 1000);
  } else {
    birthDate = new Date(dateOfBirth);
  }

  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

/**
 * Format child info for display (name + age)
 * @param {Object} child - Child object
 * @returns {string} Formatted string like "Ahmad, age 6"
 */
export const formatChildInfo = (child) => {
  if (!child || !child.name) return 'Unknown Child';

  const age = calculateAge(child.dateOfBirth);
  if (age !== null) {
    return `${child.name}, age ${age}`;
  }

  return child.name;
};

/**
 * Returns true when the user document represents an adult self-learner account.
 * Defaults to false (family) when accountType is absent — safe for all existing users.
 * @param {Object} userProfile - User document from Firestore
 * @returns {boolean}
 */
export const isAdultAccount = (userProfile) => {
  return userProfile?.accountType === 'adult';
};